import React, { useState, useEffect } from "react"
import { adminGetDictionaries, adminSaveDictionaries } from "../util/firebase"
import { TextField, Select, MenuItem, Button, IconButton, InputLabel, FormControl, Collapse } from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import FormatQuoteOutlinedIcon from "@mui/icons-material/FormatQuoteOutlined"
import DataArrayOutlinedIcon from "@mui/icons-material/DataArrayOutlined"
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined"
import "./Dictionaries.css"
import { auth } from "../util/firebase"

const Dictionaries = () => {
	const [data, setData] = useState(null)
	const [editedData, setEditedData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isSaving, setIsSaving] = useState(false)
	const [locale, setLocale] = useState("he")
	const [collapsedKeys, setCollapsedKeys] = useState({})

	const fetchData = async (locale) => {
		setIsLoading(true)
		try {
			const response = await adminGetDictionaries({ locale: locale })
			setData(response.data.content)
			setEditedData(response.data.content)
			setIsLoading(false)
		} catch (error) {
			console.error("Error fetching data:", error)
			setIsLoading(false)
		}
	}

	useEffect(() => {
		fetchData(locale)
	}, [locale])

	const handleLocaleChange = (e) => {
		setLocale(e.target.value)
	}

	const handleInputChange = (path, value) => {
		setEditedData((prevData) => {
			const newData = { ...prevData }
			const keys = path.split(".")
			let current = newData
			keys.forEach((key, index) => {
				if (index === keys.length - 1) {
					current[key] = value
				} else {
					current = current[key]
				}
			})
			return newData
		})
	}

	const handleAddKey = (path, type) => {
		const key = prompt("Enter the new key:")
		if (key) {
			setEditedData((prevData) => {
				const newData = { ...prevData }
				let value

				switch (type) {
					case "object":
						value = {}
						break
					case "array":
						value = []
						break
					case "string":
					default:
						value = ""
						break
				}

				if (path) {
					const keys = path.split(".")
					let current = newData
					keys.forEach((k, index) => {
						if (index === keys.length - 1) {
							if (!current[keys[index]]) current[keys[index]] = {}
							current[keys[index]][key] = value
						} else {
							if (!current[k]) current[k] = {}
							current = current[k]
						}
					})
				} else {
					newData[key] = value
				}
				return newData
			})
		}
	}

	const handleAddArrayElement = (path, type) => {
		setEditedData((prevData) => {
			const newData = { ...prevData }
			let value

			switch (type) {
				case "object":
					value = {}
					break
				case "array":
					value = []
					break
				case "string":
				default:
					value = ""
					break
			}

			const keys = path.split(".")
			let current = newData
			keys.forEach((key, index) => {
				if (index === keys.length - 1) {
					if (!Array.isArray(current[key])) {
						current[key] = []
					}
					current[key].push(value)
				} else {
					current = current[key]
				}
			})
			return newData
		})
	}

	const handleDeleteKey = (path) => {
		setEditedData((prevData) => {
			const newData = { ...prevData }
			const keys = path.split(".")
			let current = newData
			keys.forEach((key, index) => {
				if (index === keys.length - 1) {
					delete current[key]
				} else {
					current = current[key]
				}
			})
			return newData
		})
	}

	const handleSave = async () => {
		setIsSaving(true)
		try {
			//console.log(editedData)
			await adminSaveDictionaries({
				locale: locale,
				content: editedData,
				idToken: await auth.currentUser.getIdToken(false),
			})
			setIsSaving(false)
		} catch (error) {
			console.error("Error saving data:", error)
			setIsSaving(false)
		}
	}

	const toggleCollapse = (key) => {
		setCollapsedKeys((prevState) => ({
			...prevState,
			[key]: !prevState[key],
		}))
	}

	const renderFormFields = (obj, path = "", isTopLevel = true) => {
		return (
			<div className="form-level">
				{Object.keys(obj).map((key) => {
					const newPath = path ? `${path}.${key}` : key
					let isCollapsed = false

					// Only top-level keys are collapsed by default
					if (isTopLevel) {
						if (collapsedKeys.hasOwnProperty(newPath)) {
							isCollapsed = collapsedKeys[newPath]
						} else {
							isCollapsed = true
							setCollapsedKeys((prevState) => ({
								...prevState,
								[newPath]: true,
							}))
						}
					}

					if (isCollapsed && isTopLevel) {
						return (
							<div key={newPath} className="form-field">
								<h3 style={{ marginBottom: "auto", whiteSpace: "nowrap" }}>
									<span onClick={() => toggleCollapse(newPath)}>{isCollapsed ? "+" : "-"}</span> {key}
								</h3>
							</div>
						)
					} else {
						if (obj[key] === null) {
							return (
								<div key={newPath} className="form-field">
									<TextField
										multiline
										label={key}
										value=""
										onChange={(e) => handleInputChange(newPath, e.target.value)}
										variant="outlined"
										fullWidth
									/>
									<IconButton onClick={() => handleDeleteKey(newPath)} aria-label="delete">
										<DeleteOutlineIcon />
									</IconButton>
								</div>
							)
						} else if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
							return (
								<div key={newPath} className="form-field">
									<h3 style={{ marginBottom: "auto", whiteSpace: "nowrap" }}>
										<span onClick={() => toggleCollapse(newPath)}>{isCollapsed ? "+" : "-"}</span> {key}
									</h3>
									<Collapse sx={{ width: "100%" }} in={!isCollapsed || !isTopLevel}>
										{renderFormFields(obj[key], newPath, false)}
										<div>
											<IconButton onClick={() => handleAddKey(newPath, "object")} aria-label="add object">
												<DataObjectOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleAddKey(newPath, "array")} aria-label="add array">
												<DataArrayOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleAddKey(newPath, "string")} aria-label="add string">
												<FormatQuoteOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleDeleteKey(newPath)} aria-label="delete">
												<DeleteOutlineIcon />
											</IconButton>
										</div>
									</Collapse>
								</div>
							)
						} else if (Array.isArray(obj[key])) {
							return (
								<div key={newPath} className="form-field">
									<h3 style={{ marginBottom: "auto", whiteSpace: "nowrap" }}>
										<span onClick={() => toggleCollapse(newPath)}>{isCollapsed ? "+" : "-"}</span> {key}
									</h3>
									<Collapse sx={{ width: "100%" }} in={!isCollapsed || !isTopLevel}>
										{obj[key].map((item, index) => (
											<div key={`${newPath}.${index}`} className="form-field">
												{typeof item === "object" && item !== null ? (
													<div style={{ width: "90%" }}>
														{renderFormFields(item, `${newPath}.${index}`, false)}
														<div>
															<IconButton
																onClick={() => handleAddKey(`${newPath}.${index}`, "object")}
																aria-label="add object"
															>
																<DataObjectOutlinedIcon />
															</IconButton>
															<IconButton
																onClick={() => handleAddKey(`${newPath}.${index}`, "array")}
																aria-label="add array"
															>
																<DataArrayOutlinedIcon />
															</IconButton>
															<IconButton
																onClick={() => handleAddKey(`${newPath}.${index}`, "string")}
																aria-label="add string"
															>
																<FormatQuoteOutlinedIcon />
															</IconButton>
															<IconButton onClick={() => handleDeleteKey(`${newPath}.${index}`)} aria-label="delete">
																<DeleteOutlineIcon />
															</IconButton>
														</div>
													</div>
												) : (
													<div key={`${newPath}.${index}`} style={{ display: "flex", width: "100%" }}>
														<TextField
															multiline
															label={`${key}[${index}]`}
															value={item || ""}
															onChange={(e) => handleInputChange(`${newPath}.${index}`, e.target.value)}
															variant="outlined"
															fullWidth
														/>
														<IconButton onClick={() => handleDeleteKey(`${newPath}.${index}`)} aria-label="delete">
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												)}
											</div>
										))}
										<div>
											<IconButton onClick={() => handleAddArrayElement(newPath, "object")} aria-label="add object">
												<DataObjectOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleAddArrayElement(newPath, "array")} aria-label="add array">
												<DataArrayOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleAddArrayElement(newPath, "string")} aria-label="add string">
												<FormatQuoteOutlinedIcon />
											</IconButton>
											<IconButton onClick={() => handleDeleteKey(newPath)} aria-label="delete">
												<DeleteOutlineIcon />
											</IconButton>
										</div>
									</Collapse>
								</div>
							)
						} else {
							return (
								<div key={newPath} className="form-field">
									<TextField
										multiline
										label={key}
										value={obj[key]}
										onChange={(e) => handleInputChange(newPath, e.target.value)}
										variant="outlined"
										fullWidth
									/>
									<IconButton onClick={() => handleDeleteKey(newPath)} aria-label="delete">
										<DeleteOutlineIcon />
									</IconButton>
								</div>
							)
						}
					}
				})}
			</div>
		)
	}

	if (isLoading) {
		return <div>Loading...</div>
	}

	return (
		<div>
			<div className="locale-selector">
				<FormControl fullWidth variant="outlined">
					<InputLabel id="locale-label">Locale</InputLabel>
					<Select labelId="locale-label" id="locale" value={locale} onChange={handleLocaleChange} label="Locale">
						<MenuItem value="he">he</MenuItem>
						<MenuItem value="ar">ar</MenuItem>
						<MenuItem value="en">en</MenuItem>
						<MenuItem value="ru">ru</MenuItem>
					</Select>
				</FormControl>
			</div>
			{data && (
				<div>
					<Button
						variant="contained"
						color="primary"
						onClick={() => handleAddKey("", "object")}
						startIcon={<AddCircleOutlineIcon />}
					>
						Add Top-Level Key
					</Button>
					<form style={{ marginTop: "20px", width: "100%" }}>{renderFormFields(editedData)}</form>
					<Button
						sx={{ marginTop: "20px" }}
						variant="contained"
						color="primary"
						onClick={handleSave}
						disabled={isSaving}
					>
						{isSaving ? "Saving..." : "Save"}
					</Button>
				</div>
			)}
		</div>
	)
}

export default Dictionaries
